import { useCallback, memo } from "react";

import { clsx } from "clsx";
import { useDispatch, useSelector } from "react-redux";

import { RVIcon, RVIconVariantOptions, RVIconSizeOptions } from "@regal-voice/ui";
import { BranchNode } from "Components/elements/RVFlow/Shared/FlowNodes/BranchNode/BranchNode";
import { DynamicBranchNode } from "Components/elements/RVFlow/Shared/FlowNodes/DynamicBranchNode/DynamicBranchNode";
import { DynamicMultiBranchNode } from "Components/elements/RVFlow/Shared/FlowNodes/DynamicMultiBranchNode/DynamicMultiBranchNode";
import { InputNode } from "Components/elements/RVFlow/Shared/FlowNodes/InputNode/InputNode";
import { InputOutputNode } from "Components/elements/RVFlow/Shared/FlowNodes/InputOutputNode/InputOutputNode";
import { FlowNodeRemove } from "Components/elements/RVFlow/Shared/FlowNodes/Shared/FlowNodeRemove/FlowNodeRemove";
import {
    removeElementById,
    selectConnectingNodesFrom,
    selectSelection,
} from "Services/state/journeys/JourneyStateSlice";

import type { DynamicHandle } from "Components/elements/RVFlow/constants";

import styles from "./SharedJourneyNode.module.scss";

export const JounreyInputNode = memo(function JounreyInputNode({
    isConnectable,
    selected,
    children,
}: {
    isConnectable: boolean;
    selected: boolean;
    children: JSX.Element;
}) {
    const connectingNodesFrom = useSelector(selectConnectingNodesFrom);
    return (
        <InputNode isConnectable={isConnectable} selected={selected} connectingNodesFrom={connectingNodesFrom}>
            {children}
        </InputNode>
    );
});

const JourneyNodeRemoveIcon = memo(function JourneyNodeRemoveIcon({ id }: { id: string }) {
    const dispatch = useDispatch();
    const connectingNodesFrom = !!useSelector(selectConnectingNodesFrom);
    const handleRemove = useCallback(() => {
        dispatch(removeElementById(id));
    }, [id, dispatch]);

    return (
        <FlowNodeRemove connectingNodesFrom={connectingNodesFrom} handleRemove={handleRemove}>
            <RVIcon
                className={clsx(styles.closeIcon)}
                icon={"close"}
                variant={RVIconVariantOptions.CIRCLE}
                size={RVIconSizeOptions.SMALL}
            />
        </FlowNodeRemove>
    );
});

export const JourneyInputOutputNode = memo(function JourneyInputOutputNode({
    id,
    isConnectable,
    selected,
    children,
}: {
    id: string;
    isConnectable: boolean;
    selected: boolean;
    children: JSX.Element;
}) {
    const connectingNodesFrom = useSelector(selectConnectingNodesFrom);

    return (
        <InputOutputNode
            isConnectable={isConnectable}
            removeNodeIcon={<JourneyNodeRemoveIcon id={id} />}
            connectingNodesFrom={connectingNodesFrom}
            selected={selected}
        >
            {children}
        </InputOutputNode>
    );
});

export const JourneyBranchNode = memo(function JourneyBranchNode({
    id,
    isConnectable,
    selected,
    children,
}: {
    id: string;
    isConnectable: boolean;
    selected: boolean;
    children: JSX.Element;
}) {
    const connectingNodesFrom = useSelector(selectConnectingNodesFrom);

    return (
        <BranchNode
            id={id}
            isConnectable={isConnectable}
            selected={selected}
            connectingNodesFrom={connectingNodesFrom}
            removeNodeIcon={<JourneyNodeRemoveIcon id={id} />}
        >
            {children}
        </BranchNode>
    );
});

export const JourneyDynamicPercentageBranchNode = memo(function JourneyDynamicBranchNode({
    id,
    isConnectable,
    handles,
    children,
}: {
    id: string;
    isConnectable: boolean;
    handles: Array<DynamicHandle | undefined>;
    children: JSX.Element;
}) {
    const selection = useSelector(selectSelection);
    const connectingNodesFrom = useSelector(selectConnectingNodesFrom);
    const isSelected = (selection || []).map((el) => el.id).includes(id);

    return (
        <DynamicBranchNode
            id={id}
            handles={handles}
            isSelected={isSelected}
            isConnectable={isConnectable}
            connectingNodesFrom={connectingNodesFrom}
            removeNodeIcon={<JourneyNodeRemoveIcon id={id} />}
        >
            {children}
        </DynamicBranchNode>
    );
});

/**
 * Similar to @JourneyDynamicPercentageBranchNode used for A/B Test node,
 * but this one supports proper text labels instead of percentages
 * and provides a background for labels.
 */
export const JourneyDynamicBranchNode = memo(function JourneyDynamicBranchNode({
    id,
    isConnectable,
    handles,
    children,
}: {
    id: string;
    isConnectable: boolean;
    handles: Array<DynamicHandle | undefined>;
    children: JSX.Element;
}) {
    const selection = useSelector(selectSelection);
    const connectingNodesFrom = useSelector(selectConnectingNodesFrom);
    const isSelected = (selection || []).map((el) => el.id).includes(id);

    return (
        <DynamicMultiBranchNode
            id={id}
            handles={handles}
            isSelected={isSelected}
            isConnectable={isConnectable}
            connectingNodesFrom={connectingNodesFrom}
            removeNodeIcon={<JourneyNodeRemoveIcon id={id} />}
            minimumVisibleHandles={0}
        >
            {children}
        </DynamicMultiBranchNode>
    );
});
